import React, { useMemo, useState } from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import { useSessionStore } from "../../../Stores/SessionStore";
import { RegistrationSteps } from "../Registration";
import StepContainer from "./StepContainer";
import GradientButton from "../../Core/GradientButton";

const djb2Hash = (str) => {
	let hash = 5381;
	for (let i = 0; i < str.length; i++) {
		hash = (hash << 5) + hash + str.charCodeAt(i);
		hash = hash & 0xffffffff;
	}
	return Math.abs(hash).toString(36);
};

const deepStableSort = (val) => {
	if (Array.isArray(val)) return val.map(deepStableSort);
	if (val && typeof val === "object") {
		const sorted = {};
		Object.keys(val)
			.sort()
			.forEach((k) => {
				sorted[k] = deepStableSort(val[k]);
			});
		return sorted;
	}
	return val;
};

function getAddressLabel(address) {
	if (!address) return "";
	const segments = [address.StreetAddress];
	if (address.MDUName) segments.push(address.MDUName);
	if (address.MDUBlock) segments.push(`BLOCK ${address.MDUBlock}`);
	if (address.MDUUnitNumber) segments.push(`UNIT ${address.MDUUnitNumber}`);
	if (address.Township) segments.push(address.Township);
	return `ZNG ${address.ServiceId} | ${segments.filter(Boolean).join(", ")} | ${address.ERF ?? ""}`;
}

function DetailRow({ label, value }) {
	return (
		<Row className="mb-2 mb-sm-3">
			<Col sm={4}>
				<b>{label}</b>
			</Col>
			<Col sm={8}>{value}</Col>
		</Row>
	);
}

export default function ConfirmCustomerDetails(props) {
	const store = useSessionStore();
	const [confirmDetails, setConfirmDetails] = useState(false);
	const [showingConfirmationError, setShowingConfirmationError] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState(null);
	const [duplicateError, setDuplicateError] = useState(false);

	const idempotencyKey = useMemo(() => {
		const payload = {
			details: deepStableSort(props.details ?? {}),
			addressId: props.address?.Id ?? null,
			addressUsername: props.address?.Username ?? null,
		};
		return djb2Hash(JSON.stringify(payload));
	}, [props.details, props.address?.Id, props.address?.Username]);

	const storageKey = `registration:${idempotencyKey}`;

	if (!props.details) {
		return (
			<StepContainer title="Confirmation of customer details:">
				<Row>
					<Col className="text-center">
						<p>Missing details.</p>
					</Col>
				</Row>
			</StepContainer>
		);
	}

	const phoneDigits = props.details.CellphoneNumber ?? "";
	const formattedPhone = `0${phoneDigits.length === 9 ? phoneDigits : phoneDigits.substring(1)}`;
	const idLabel = props.details.IDNumberPreference === "ID" ? "ID Number" : "Foreign Passport";
	const addressValue = props.address ? getAddressLabel(props.address) : props.details.PotentialAddress;

	function getStatus() {
		try {
			const raw = sessionStorage.getItem(storageKey);
			return raw ? JSON.parse(raw) : null;
		} catch {
			return null;
		}
	}

	function setStatus(status) {
		try {
			sessionStorage.setItem(storageKey, JSON.stringify({ status, ts: Date.now() }));
		} catch {
			// ignore storage failures; idempotency is best-effort
		}
	}

	async function submitRegistration() {
		setSubmitError(null);
		setDuplicateError(false);
		setSubmitting(true);

		const previous = getStatus();
		if (previous?.status === "done") {
			setSubmitting(false);
			props.onContinue(null, RegistrationSteps.CustomerDetailsConfirmation.Step);
			return;
		}
		if (previous?.status === "inflight" && previous?.ts && Date.now() - previous.ts < 90_000) {
			setSubmitting(false);
			return;
		}

		const normalizedSalesAgentId =
			props.details.SalesAgentId && String(props.details.SalesAgentId).trim().length > 0
				? props.details.SalesAgentId
				: null;

		const customerRegistration = {
			Customer: {
				...props.details,
				SalesAgentId: normalizedSalesAgentId,
				Username: props.address?.Username,
				AddressId: props.address?.Id,
			},
			RadiusAddress: props.address,
			RegistrationSource: "web",
			IdempotencyKey: idempotencyKey,
		};

		setStatus("inflight");
		const result = await store.CustomersService.RegisterCustomer(customerRegistration);
		setSubmitting(false);

		if (result?.Success) {
			setStatus("done");
			props.onContinue(null, RegistrationSteps.CustomerDetailsConfirmation.Step);
			return;
		}

		try {
			sessionStorage.removeItem(storageKey);
		} catch {
			// ignore
		}
		const message = result?.Message ?? "Registration failed. Please try again.";
		setSubmitError(message);
		if (typeof message === "string" && message.toLowerCase().includes("already exists")) {
			setDuplicateError(true);
		}
	}

	function handleSubmit() {
		if (!confirmDetails) {
			setShowingConfirmationError(true);
			return;
		}
		setShowingConfirmationError(false);
		submitRegistration();
	}

	function handleStartOver() {
		props.onContinue(props.address ?? null, RegistrationSteps.AddressSelection.Step);
	}

	return (
		<StepContainer title="Confirmation of customer details:">
			<Row>
				<Col>
					<DetailRow label="First Name" value={props.details.FirstName} />
					<DetailRow label="Surname" value={props.details.Surname} />
					{(props.address || props.details.PotentialAddress) && (
						<DetailRow label="Address" value={addressValue} />
					)}
					<DetailRow label="Email" value={props.details.Email} />
					<DetailRow label="WhatsApp Number" value={formattedPhone} />
					<DetailRow label={idLabel} value={props.details.IDNumber} />

					<Row className="mt-4">
						<Col className="d-flex justify-content-center">
							<div className="form-check">
								<input
									checked={confirmDetails}
									onChange={(e) => setConfirmDetails(e.target.checked)}
									type="checkbox"
									className="form-check-input"
									id="confirmDetails"
								/>
								<label htmlFor="confirmDetails" className="form-check-label">
									I confirm the details I have entered are correct to the best of my knowledge and that the address I am
									registering for is tenanted or owned by myself or another family member.
								</label>
							</div>
							{showingConfirmationError && (
								<div className="text-center mt-2">
									<small className="text-danger">Confirmation of details is required</small>
								</div>
							)}
						</Col>
					</Row>

					{submitError && (
						<Row className="mt-3 text-center">
							<Col>
								<small className="text-danger">{submitError}</small>
							</Col>
						</Row>
					)}

					{submitting ? (
						<Row className="mt-4 text-center">
							<Col>
								<Spinner animation="border" variant="success" />
								<p className="mt-2 mb-0">Submitting registration...</p>
							</Col>
						</Row>
					) : (
						<Row className="mt-4">
							<Col xs={12} lg={6} className="offset-xs-0 offset-lg-3">
								{duplicateError ? (
									<GradientButton title="Start Again" variant="yellowOrange" onClick={handleStartOver} />
								) : (
									<GradientButton title="Submit" variant="greenYellow" onClick={handleSubmit} />
								)}
							</Col>
						</Row>
					)}
				</Col>
			</Row>
		</StepContainer>
	);
}
